import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { cuplAPI } from '../context/api';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import CUPLTable from '../components/CUPLTable';
import EditModal from '../components/EditModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import PayConfirmModal from '../components/PayConfirmModal';
import RegisterCUPLModal from '../components/RegisterCUPLModal';
import ImportExcelModal from '../components/ImportExcelModal';
import ReceiptModal from '../components/ReceiptModal';
import BatchPaymentModal from '../components/BatchPaymentModal';
import axios, { API_URL } from '../api/axiosConfig';
import './Dashboard.css';
import './CuplPayments.css';

const CuplPayments = () => {
  const { user, addApiLog } = useContext(AuthContext);
  const navigate = useNavigate();
  const [cupls, setCupls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [editingCUPL, setEditingCUPL] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [startDate] = useState('');
  const [endDate] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cuplToDelete, setCuplToDelete] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [balance, setBalance] = useState(null);
  const [showPayConfirmModal, setShowPayConfirmModal] = useState(false);
  const [cuplToPay, setCuplToPay] = useState(null);
  const [paying, setPaying] = useState(false);
  const [processingRuntId, setProcessingRuntId] = useState(null);
  const [previewCupl, setPreviewCupl] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  // Estados para selección por lote
  const [selectedCuplIds, setSelectedCuplIds] = useState([]);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchAction, setBatchAction] = useState('runt'); // 'runt' o 'manual'
  const [selectQty, setSelectQty] = useState('');

  useEffect(() => {
    fetchCUPLs();
    fetchStatistics();
    fetchBalance();

    const balanceInterval = setInterval(() => {
      fetchBalance();
    }, 5000);

    const handleFocus = () => {
      fetchBalance();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(balanceInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await axios.get('/api/cash/balance');
      const balanceValue = parseFloat(response.data.balance) || 0;
      setBalance(balanceValue);
    } catch (err) {
      console.error('Error al obtener balance:', err);
      setBalance(0);
    }
  };

  const fetchCUPLs = async () => {
    try {
      setLoading(true);
      const response = await cuplAPI.getAllCUPL();
      setCupls(response.data);
      setError('');
    } catch (err) {
      setError('Error al cargar los registros CUPL');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await cuplAPI.getStatistics();
      setStats(response.data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const handleEditCUPL = (cupl) => {
    setEditingCUPL(cupl);
    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setEditingCUPL(null);
    setShowEditModal(false);
  };

  const handleSaveEdit = async (data) => {
    setSavingEdit(true);
    try {
      await cuplAPI.updateCUPL(editingCUPL.id, data);
      setEditingCUPL(null);
      setShowEditModal(false);
      setError('');
      fetchCUPLs();
      fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar CUPL');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteCUPL = (cupl) => {
    setCuplToDelete(cupl);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!cuplToDelete) return;
    setDeleting(true);
    try {
      await cuplAPI.deleteCUPL(cuplToDelete.id);
      setCuplToDelete(null);
      setShowDeleteModal(false);
      setError('');
      fetchCUPLs();
      fetchStatistics();
    } catch (err) {
      setError('Error al eliminar CUPL');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setCuplToDelete(null);
    setShowDeleteModal(false);
  };

  const handleOpenRegisterModal = () => {
    setShowRegisterModal(true);
  };

  const handleCloseRegisterModal = () => {
    setShowRegisterModal(false);
  };

  const handleRegisterFromModal = async (data) => {
    setRegistering(true);
    try {
      await cuplAPI.createCUPL(data);
      setShowRegisterModal(false);
      setError('');
      fetchCUPLs();
      fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar CUPL');
    } finally {
      setRegistering(false);
    }
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
  };

  const handleMarkAsPaid = (id) => {
    const cupl = cupls.find(c => c.id === id);
    if (cupl) {
      setCuplToPay(cupl);
      setShowPayConfirmModal(true);
    }
  };

  const handleConfirmPay = async () => {
    if (!cuplToPay) return;
    setPaying(true);
    try {
      await cuplAPI.markAsPaid(cuplToPay.id);
      setCuplToPay(null);
      setShowPayConfirmModal(false);
      setError('');
      fetchCUPLs();
      fetchStatistics();
      fetchBalance();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al marcar como pagado');
    } finally {
      setPaying(false);
    }
  };

  const handleCancelPay = () => {
    setCuplToPay(null);
    setShowPayConfirmModal(false);
  };

  const handleRejectCUPL = async (cupl) => {
    const reason = window.prompt(`¿Por qué deseas rechazar el CUPL ${cupl.cupl_number}?`, 'Documento Vencido');
    if (reason === null) return;
    try {
      await cuplAPI.markAsRejected(cupl.id, reason);
      alert('✅ CUPL marcado como rechazado y notificación enviada.');
      fetchCUPLs();
      fetchStatistics();
    } catch (err) {
      console.error('Error al rechazar CUPL:', err);
      setError(err.response?.data?.message || 'Error al rechazar CUPL');
    }
  };

  const handlePagoRunt = async (cupl) => {
    if (!window.confirm(`¿Deseas realizar el PAGO AUTOMÁTICO en RUNT para el CUPL ${cupl.cupl_number}?`)) {
      return;
    }
    setProcessingRuntId(cupl.id);
    setError('');
    try {
      const data = await processSinglePayment(cupl);
      if (data) {
        setReceiptData({
          ...cupl,
          paid_at: new Date().toISOString(),
          external_id: data.detalles?.resultado_pago?.transaccion_externa || data.db_id
        });
        setShowReceipt(true);
        alert('✅ ' + data.mensaje);
      }
    } catch (err) {
      // El error ya se maneja dentro de processSinglePayment
    } finally {
      setProcessingRuntId(null);
    }
  };

  const processSinglePayment = async (cupl) => {
    const startTime = new Date();
    addApiLog(cupl.cupl_number, 'REQUEST', `Iniciando petición de pago para referencia ${cupl.cupl_number}`, 'pending');
    try {
      const runtApiUrl = localStorage.getItem('runt_api_url') || process.env.REACT_APP_RUNT_API_URL || 'http://26.2.248.216:8051';
      const runtApiKey = localStorage.getItem('runt_api_key') || process.env.REACT_APP_RUNT_API_KEY || 'TU_API_KEY_AQUI';
      
      const response = await axios.post('/api/automation/proxy-pago-runt', {
        referencia: cupl.cupl_number,
        valor_ingresado: 0,
        apiUrl: runtApiUrl,
        apiKey: runtApiKey
      });

      const duration = new Date() - startTime;
      addApiLog(cupl.cupl_number, 'RESPONSE', `Respuesta recibida en ${duration}ms. ${response.data.mensaje}`, 'success');

      if (response.status === 200) {
        await cuplAPI.markAsPaid(cupl.id);
        fetchCUPLs();
        fetchStatistics();
        fetchBalance();
        return response.data;
      }
    } catch (err) {
      const duration = new Date() - startTime;
      let errorDetail = err.response?.data?.detalles?.resultado_pago?.mensaje || err.response?.data?.mensaje || err.response?.data?.message || err.message || 'Error desconocido';
      addApiLog(cupl.cupl_number, 'ERROR', `Falla después de ${duration}ms: ${errorDetail}`, 'error');
      setError(`❌ Error RUNT (${cupl.cupl_number}): ${errorDetail}`);
      throw err;
    }
  };

  // Handlers para selección
  const handleSelectCupl = (id) => {
    setSelectedCuplIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (itemsOnPage, checked) => {
    if (checked) {
      const idsToAdd = itemsOnPage
        .filter(c => !c.is_paid && !c.is_rejected)
        .map(c => c.id);
      setSelectedCuplIds(prev => [...new Set([...prev, ...idsToAdd])]);
    } else {
      const idsToRemove = itemsOnPage.map(c => c.id);
      setSelectedCuplIds(prev => prev.filter(id => !idsToRemove.includes(id)));
    }
  };

  const handleSelectByQty = () => {
    const qty = parseInt(selectQty);
    if (isNaN(qty) || qty <= 0) return;

    const pendings = getFilteredCUPLs().filter(c => !c.is_paid && !c.is_rejected);
    const toSelect = pendings.slice(0, qty).map(c => c.id);
    setSelectedCuplIds(toSelect);
    setSelectQty('');
  };

  const handleOpenBatchModal = (action = 'runt') => {
    if (selectedCuplIds.length === 0) return;
    setBatchAction(action);
    setShowBatchModal(true);
  };

  const handlePreviewPDF = (cupl) => {
    setPreviewCupl(cupl);
  };

  const handleViewReceipt = (cupl) => {
    setReceiptData(cupl);
    setShowReceipt(true);
  };

  const getFilteredCUPLs = () => {
    let filtered = cupls;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(cupl =>
        cupl.cupl_number.toLowerCase().includes(term) ||
        cupl.plate.toLowerCase().includes(term) ||
        cupl.owner_name.toLowerCase().includes(term)
      );
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (statusFilter === 'pagado') {
      filtered = filtered.filter(cupl => cupl.is_paid);
    } else if (statusFilter === 'pendiente') {
      filtered = filtered.filter(cupl => {
        if (cupl.is_paid || cupl.is_rejected) return false;
        if (!cupl.expiration_date) return true;
        const expDate = new Date(cupl.expiration_date);
        expDate.setHours(0, 0, 0, 0);
        return expDate >= today;
      });
    } else if (statusFilter === 'vencido') {
      filtered = filtered.filter(cupl => {
        if (cupl.is_paid) return false;
        const isRejectedExpired = cupl.rejection_reason === 'Documento Vencido';
        if (isRejectedExpired) return true;
        if (!cupl.expiration_date) return false;
        const expDate = new Date(cupl.expiration_date);
        expDate.setHours(0, 0, 0, 0);
        return expDate < today;
      });
    } else if (statusFilter === 'rechazado') {
      filtered = filtered.filter(cupl => {
        if (cupl.is_paid || !cupl.is_rejected) return false;
        const isRejectedExpired = cupl.rejection_reason === 'Documento Vencido';
        if (isRejectedExpired) return false;
        if (cupl.expiration_date) {
          const expDate = new Date(cupl.expiration_date);
          expDate.setHours(0, 0, 0, 0);
          if (expDate < today) return false;
        }
        return true;
      });
    }
    if (startDate) {
      const [year, month, day] = startDate.split('-').map(Number);
      const start = new Date(year, month - 1, day, 0, 0, 0, 0);
      filtered = filtered.filter(cupl => new Date(cupl.created_at) >= start);
    }
    if (endDate) {
      const [year, month, day] = endDate.split('-').map(Number);
      const end = new Date(year, month - 1, day, 23, 59, 59, 999);
      filtered = filtered.filter(cupl => new Date(cupl.created_at) <= end);
    }
    return filtered;
  };

  const filteredCupls = getFilteredCUPLs();

  return (
    <div className="app-layout">
      <Sidebar userRole={user?.role} />
      <div className="main-content">
        <Navbar title="💳 Gestión de Pagos CUPL" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          {stats && (
            <div className="stats-grid">
              <div className="stat-card info">
                <div className="stat-icon">📑</div>
                <div className="stat-details">
                  <span className="stat-label">Total Registrados</span>
                  <span className="stat-value">{stats.total_registrados}</span>
                </div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">✅</div>
                <div className="stat-details">
                  <span className="stat-label">Pagados</span>
                  <span className="stat-value">{stats.total_pagados}</span>
                </div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">⏳</div>
                <div className="stat-details">
                  <span className="stat-label">Pendientes</span>
                  <span className="stat-value">{stats.total_pendientes}</span>
                </div>
              </div>
              <div className="stat-card danger">
                <div className="stat-icon">🚫</div>
                <div className="stat-details">
                  <span className="stat-label">Vencidos</span>
                  <span className="stat-value">{stats.total_vencidos || 0}</span>
                </div>
              </div>
            </div>
          )}

          <div className="action-buttons cupl-action-buttons">
            <div className="cupl-left-actions">
              <button onClick={handleOpenRegisterModal} className="btn-primary">📝 Registrar CUPL</button>
              {user?.role === 'superadmin' && <button onClick={handleOpenImportModal} className="btn-secondary">📥 Importar Excel</button>}
              
              <div className="select-qty-container">
                <span className="select-qty-label">Seleccionar:</span>
                <input 
                  type="number" 
                  placeholder="Cant." 
                  value={selectQty} 
                  onChange={(e) => setSelectQty(e.target.value)}
                  className="select-qty-input"
                />
                <button 
                  onClick={handleSelectByQty}
                  className="btn-secondary btn-qty-ok"
                >
                  OK
                </button>
              </div>

              {selectedCuplIds.length > 0 && (
                <>
                  <button 
                    onClick={() => handleOpenBatchModal('runt')} 
                    className="btn-success btn-batch-runt"
                  >
                    🚀 Pagar RUNT ({selectedCuplIds.length})
                    <span className="batch-amount-span">
                      [${cupls.filter(c => selectedCuplIds.includes(c.id)).reduce((s, c) => s + (parseFloat(c.total_amount || c.tramite_value) || 0), 0).toLocaleString('es-CO')}]
                    </span>
                  </button>

                  <button 
                    onClick={() => handleOpenBatchModal('manual')} 
                    className="btn-warning btn-batch-manual"
                  >
                    📝 Marcar Pagados ({selectedCuplIds.length})
                  </button>
                </>
              )}
            </div>
            <button className="btn-secondary btn-logs-api" onClick={() => navigate('/api-logs')}>📜 Ver Logs API</button>
          </div>

          <div className="table-section">
            <div className="filters-container">
              <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="status-select">
                <option value="todos">Todos los estados</option>
                <option value="pagado">Pagados</option>
                <option value="pendiente">Pendientes</option>
                <option value="vencido">Vencidos</option>
                <option value="rechazado">Rechazados</option>
              </select>
            </div>

            {loading ? <p className="loading">Cargando...</p> : (
              <CUPLTable
                cupls={filteredCupls}
                onEdit={handleEditCUPL}
                onMarkPaid={handleMarkAsPaid}
                onReject={handleRejectCUPL}
                onDelete={handleDeleteCUPL}
                userRole={user?.role}
                balance={balance}
                onPreviewPDF={handlePreviewPDF}
                onPagoRunt={handlePagoRunt}
                onViewReceipt={handleViewReceipt}
                processingRuntId={processingRuntId}
                selectedCuplIds={selectedCuplIds}
                onSelectCupl={handleSelectCupl}
                onSelectAll={handleSelectAll}
              />
            )}
          </div>
        </div>
      </div>

      <RegisterCUPLModal isOpen={showRegisterModal} onClose={handleCloseRegisterModal} onSubmit={handleRegisterFromModal} loading={registering} />
      <ImportExcelModal isOpen={showImportModal} onClose={handleCloseImportModal} onImportSuccess={fetchCUPLs} />
      <EditModal isOpen={showEditModal} cupl={editingCUPL} onClose={handleCancelEdit} onSave={handleSaveEdit} loading={savingEdit} />
      <DeleteConfirmModal isOpen={showDeleteModal} cupl={cuplToDelete} onClose={handleCancelDelete} onConfirm={handleConfirmDelete} loading={deleting} />
      <PayConfirmModal isOpen={showPayConfirmModal} cupl={cuplToPay} onClose={handleCancelPay} onConfirm={handleConfirmPay} loading={paying} />
      <ReceiptModal isOpen={showReceipt} onClose={() => setShowReceipt(false)} data={receiptData} />
      
      <BatchPaymentModal 
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          setSelectedCuplIds([]);
        }}
        selectedCupls={cupls.filter(c => selectedCuplIds.includes(c.id))}
        onProcessPayment={batchAction === 'runt' ? processSinglePayment : (cupl) => cuplAPI.markAsPaid(cupl.id)}
        balance={balance}
        title={batchAction === 'runt' ? "Pago Automático RUNT" : "Marcado Manual como Pagado"}
        actionLabel={batchAction === 'runt' ? "Iniciar Pagos RUNT" : "Marcar todos como Pagados"}
        onComplete={() => {
          fetchCUPLs();
          fetchStatistics();
          fetchBalance();
        }}
      />

      {previewCupl && (
        <div className="modal-overlay" onClick={() => setPreviewCupl(null)}>
          <div className="modal-content pdf-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Previsualización PDF - {previewCupl.cupl_number}</h3>
              <button className="close-btn" onClick={() => setPreviewCupl(null)}>&times;</button>
            </div>
            <div style={{ height: '80vh' }}>
              <iframe 
                title={`Vista previa del CUPL ${previewCupl.cupl_number}`}
                src={`${API_URL}/api/automation/preview-pdf/${previewCupl.cupl_number}?token=${localStorage.getItem('token')}`} 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuplPayments;
